import React, { useState, useEffect } from 'react';
import { X, Sparkle, CheckCircle, Spinner, Browser, Copy, Plus, Minus, Code, Rocket, Info } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api from '@/utils/api';
import WhyThisFitsYouBlock from './WhyThisFitsYouBlock';
import { getUser } from '@/utils/auth';
import { useWorkflowStore } from '@/store/workflowStore';
import LaunchPlanCard from '@/components/LaunchPlanCard';
import OutputActions from '@/components/OutputActions';
import ToolLinks from './ToolLinks';

const SalesPageModal = ({ isOpen, onClose, onSuccess, onAction }) => {
  const user = getUser();
  const [step, setStep] = useState('input'); // 'input', 'generating', 'result'
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [showHtml, setShowHtml] = useState(false);
  const [showLaunchPlan, setShowLaunchPlan] = useState(false);
  const [hasPrefilledFromOffer, setHasPrefilledFromOffer] = useState(false);
  
  // Zustand workflow store
  const workflowStore = useWorkflowStore();
  const { 
    offerName, 
    offerDescription, 
    audience, 
    benefits: storedBenefits, 
    bonuses: storedBonuses, 
    price: storedPrice,
    offerCreated,
    setSalesPageData,
    resetCompletionFlags
  } = workflowStore;
  
  const [formData, setFormData] = useState({
    offer_name: '',
    offer_description: '',
    target_audience: '',
    key_benefits: ['', '', ''],
    price: '',
    bonuses: ['']
  });

  // Auto-fill from workflow store when modal opens
  useEffect(() => {
    if (isOpen && !hasPrefilledFromOffer && offerCreated) {
      // Pre-fill form with offer data from Zustand store
      const benefits = storedBenefits?.slice(0, 5) || ['', '', ''];
      // Ensure we have at least 3 benefit slots
      while (benefits.length < 3) {
        benefits.push('');
      }
      
      const bonuses = storedBonuses?.slice(0, 3) || [''];
      // Ensure we have at least 1 bonus slot
      if (bonuses.length === 0) {
        bonuses.push('');
      }
      
      setFormData({
        offer_name: offerName || '',
        offer_description: offerDescription || '',
        target_audience: audience || '',
        key_benefits: benefits,
        price: storedPrice || '',
        bonuses: bonuses
      });
      
      setHasPrefilledFromOffer(true);
    }
  }, [isOpen, hasPrefilledFromOffer, offerCreated, offerName, offerDescription, audience, storedBenefits, storedBonuses, storedPrice]);

  const handleBenefitChange = (index, value) => {
    const newBenefits = [...formData.key_benefits];
    newBenefits[index] = value;
    setFormData({...formData, key_benefits: newBenefits});
  };

  const addBenefit = () => {
    if (formData.key_benefits.length < 7) {
      setFormData({...formData, key_benefits: [...formData.key_benefits, '']});
    }
  };

  const removeBenefit = (index) => {
    if (formData.key_benefits.length > 2) {
      const newBenefits = formData.key_benefits.filter((_, i) => i !== index);
      setFormData({...formData, key_benefits: newBenefits});
    }
  };

  const handleBonusChange = (index, value) => {
    const newBonuses = [...formData.bonuses];
    newBonuses[index] = value;
    setFormData({...formData, bonuses: newBonuses});
  };

  const addBonus = () => {
    if (formData.bonuses.length < 5) {
      setFormData({...formData, bonuses: [...formData.bonuses, '']});
    }
  };

  const handleSubmit = async () => {
    const benefits = formData.key_benefits.filter(b => b.trim());
    const bonuses = formData.bonuses.filter(b => b.trim());
    
    if (!formData.offer_name.trim() || !formData.offer_description.trim() || 
        !formData.target_audience.trim() || benefits.length < 2 || !formData.price.trim()) {
      toast.error('Please fill in all required fields (at least 2 benefits)');
      return;
    }

    setStep('generating');
    setLoading(true);

    try {
      const response = await api.post('/monetization/sales-page', {
        ...formData,
        key_benefits: benefits,
        bonuses: bonuses.length > 0 ? bonuses : null
      });
      setResult(response.data);
      setStep('result');
      
      // Mark sales page as created in workflow store
      setSalesPageData({ id: response.data.id });
      
      if (onSuccess) onSuccess();
      toast.success('Sales page copy ready!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate sales page');
      setStep('input');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Helper to format list or string into bullet points
  const formatAsBulletPoints = (data) => {
    if (Array.isArray(data)) {
      return data.map(item => `• ${item}`).join('\n');
    }
    return data || '';
  };

  // Helper to render list or string with bullet styling
  const renderSection = (data) => {
    if (Array.isArray(data)) {
      return (
        <ul className="space-y-1">
          {data.map((item, i) => (
            <li key={i} className="flex items-start space-x-2">
              <span className="text-purple-400 mt-0.5">•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    }
    return <span className="whitespace-pre-wrap">{data}</span>;
  };

  const copyAllSections = () => {
    if (!result) return;
    const benefitsText = formatAsBulletPoints(result.benefits_section);
    const bonusesText = formatAsBulletPoints(result.bonuses_section);
    const allContent = `HEADLINE: ${result.headline}\n\nSUBHEADLINE: ${result.subheadline}\n\n---\n\nTHE PROBLEM:\n${result.problem_section}\n\n---\n\nTHE SOLUTION:\n${result.solution_section}\n\n---\n\nBENEFITS:\n${benefitsText}\n\n---\n\nWHAT'S INCLUDED:\n${result.offer_section}\n\n---\n\nBONUSES:\n${bonusesText}\n\n---\n\nCALL TO ACTION:\n${result.cta_section}`;
    navigator.clipboard.writeText(allContent);
    toast.success('All sections copied!');
  };

  const handleClose = () => {
    setStep('input');
    setResult(null);
    setShowHtml(false);
    setShowLaunchPlan(false);
    setHasPrefilledFromOffer(false);
    setFormData({
      offer_name: '',
      offer_description: '',
      target_audience: '',
      key_benefits: ['', '', ''],
      price: '',
      bonuses: ['']
    });
    onClose();
  };

  const handleLaunchAction = (action) => {
    if (onAction) {
      onAction(action);
    }
    handleClose();
  };

  const handleCreateAnother = () => {
    setStep('input');
    setResult(null);
    setShowHtml(false);
    setShowLaunchPlan(false);
    setHasPrefilledFromOffer(false);
    // Reset completion flags but keep offer data for re-use
    resetCompletionFlags();
    setFormData({
      offer_name: '',
      offer_description: '',
      target_audience: '',
      key_benefits: ['', '', ''],
      price: '',
      bonuses: ['']
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-2xl paygbot-modal-shell bg-slate-900 rounded-2xl shadow-2xl border border-white/10"
        >
          {/* Header */}
          <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <Browser size={20} className="text-white" weight="fill" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Sales Page Generator</h2>
                <p className="text-sm text-slate-400">Create high-converting sales copy</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X size={18} className="text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Input Step */}
            {step === 'input' && (
              <div className="space-y-5">
                {/* Auto-fill banner */}
                {hasPrefilledFromOffer && offerName && (
                  <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30 flex items-center space-x-3">
                    <Info size={18} className="text-purple-400 flex-shrink-0" />
                    <p className="text-purple-300 text-sm">
                      Using details from your offer: <span className="font-medium text-purple-400">{offerName}</span>
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Offer Name *
                  </label>
                  <input
                    type="text"
                    value={formData.offer_name}
                    onChange={(e) => setFormData({...formData, offer_name: e.target.value})}
                    data-testid="sales-offer-name"
                    placeholder="e.g., YouTube Mastery Bundle"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    What are you selling? *
                  </label>
                  <textarea
                    value={formData.offer_description}
                    onChange={(e) => setFormData({...formData, offer_description: e.target.value})}
                    data-testid="sales-offer-desc"
                    placeholder="Describe your product/service..."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    rows={2}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Who is this for? *
                  </label>
                  <input
                    type="text"
                    value={formData.target_audience}
                    onChange={(e) => setFormData({...formData, target_audience: e.target.value})}
                    data-testid="sales-audience"
                    placeholder="e.g., Beginners who want to start a YouTube channel"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-slate-300">Key Benefits * (min 2)</label>
                    <button
                      onClick={addBenefit}
                      disabled={formData.key_benefits.length >= 7}
                      className="text-purple-400 hover:text-purple-300 text-sm flex items-center space-x-1 disabled:opacity-50"
                    >
                      <Plus size={14} />
                      <span>Add</span>
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.key_benefits.map((benefit, i) => (
                      <div key={i} className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={benefit}
                          onChange={(e) => handleBenefitChange(i, e.target.value)}
                          placeholder={`Benefit ${i + 1}`}
                          className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 text-sm"
                        />
                        {formData.key_benefits.length > 2 && (
                          <button
                            onClick={() => removeBenefit(i)}
                            className="text-slate-500 hover:text-red-400"
                          >
                            <Minus size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Price *
                  </label>
                  <input
                    type="text"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    data-testid="sales-price"
                    placeholder="e.g., $97"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-slate-300">Bonuses (optional)</label>
                    <button
                      onClick={addBonus}
                      disabled={formData.bonuses.length >= 5}
                      className="text-purple-400 hover:text-purple-300 text-sm flex items-center space-x-1 disabled:opacity-50"
                    >
                      <Plus size={14} />
                      <span>Add</span>
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.bonuses.map((bonus, i) => (
                      <input
                        key={i}
                        type="text"
                        value={bonus}
                        onChange={(e) => handleBonusChange(i, e.target.value)}
                        placeholder={`Bonus ${i + 1}`}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 text-sm"
                      />
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  data-testid="sales-page-submit"
                  className="w-full py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-xl flex items-center justify-center space-x-2 transition-all"
                >
                  <Sparkle size={20} weight="fill" />
                  <span>Generate Sales Page</span>
                </button>
              </div>
            )}

            {/* Generating Step */}
            {step === 'generating' && (
              <div className="py-12 text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Spinner size={32} className="text-white animate-spin" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Creating Your Sales Page</h3>
                <p className="text-slate-400">Writing high-converting copy for {formData.offer_name}...</p>
              </div>
            )}

            {/* Result Step */}
            {step === 'result' && result && (
              <div className="space-y-5">
                {/* Round 92 — Why This Fits You + feedback */}
                <WhyThisFitsYouBlock
                  department="offer"
                  taskType="sales_page"
                  recommendationId={result?.id || result?.page_id || null}
                  recommendationSummary={result?.headline ? `a sales page led with "${result.headline}"` : "this sales page"}
                  businessCtx={{
                    primary_offer: result?.offer_name,
                    target_audience: result?.audience,
                  }}
                />

                <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <Browser size={24} className="text-purple-400 mb-1" />
                      <p className="text-purple-400 font-medium">Sales Page Ready!</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={copyAllSections}
                        className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-sm font-medium flex items-center space-x-1"
                      >
                        <Copy size={14} />
                        <span>Copy All</span>
                      </button>
                      <button
                        onClick={() => setShowHtml(!showHtml)}
                        className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-slate-300 rounded-lg text-sm font-medium flex items-center space-x-1"
                      >
                        <Code size={14} />
                        <span>{showHtml ? 'Hide' : 'Show'} HTML</span>
                      </button>
                    </div>
                  </div>
                </div>

                {showHtml ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Ready-to-Use HTML</label>
                      <button
                        onClick={() => handleCopy(result.full_page_html, 'html')}
                        className="text-purple-400 hover:text-purple-300 text-sm flex items-center space-x-1"
                      >
                        {copiedField === 'html' ? <CheckCircle size={14} /> : <Copy size={14} />}
                        <span>Copy HTML</span>
                      </button>
                    </div>
                    <pre className="text-slate-300 bg-black/50 p-4 rounded-lg text-xs overflow-x-auto max-h-96">
                      {result.full_page_html}
                    </pre>
                  </div>
                ) : (
                  <>
                    {/* Headline */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Headline</label>
                        <button
                          onClick={() => handleCopy(result.headline, 'headline')}
                          className="text-purple-400 hover:text-purple-300 text-sm flex items-center space-x-1"
                        >
                          {copiedField === 'headline' ? <CheckCircle size={14} /> : <Copy size={14} />}
                          <span>Copy</span>
                        </button>
                      </div>
                      <p className="text-white text-xl font-bold bg-white/5 p-3 rounded-lg">{result.headline}</p>
                    </div>

                    {/* Subheadline */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Subheadline</label>
                        <button
                          onClick={() => handleCopy(result.subheadline, 'subheadline')}
                          className="text-purple-400 hover:text-purple-300 text-sm flex items-center space-x-1"
                        >
                          {copiedField === 'subheadline' ? <CheckCircle size={14} /> : <Copy size={14} />}
                          <span>Copy</span>
                        </button>
                      </div>
                      <p className="text-purple-400 bg-purple-500/10 p-3 rounded-lg">{result.subheadline}</p>
                    </div>

                    {/* Problem Section */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">The Problem</label>
                        <button
                          onClick={() => handleCopy(result.problem_section, 'problem')}
                          className="text-purple-400 hover:text-purple-300 text-sm flex items-center space-x-1"
                        >
                          {copiedField === 'problem' ? <CheckCircle size={14} /> : <Copy size={14} />}
                          <span>Copy</span>
                        </button>
                      </div>
                      <p className="text-slate-300 bg-white/5 p-3 rounded-lg text-sm whitespace-pre-wrap">{result.problem_section}</p>
                    </div>

                    {/* Solution Section */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">The Solution</label>
                        <button
                          onClick={() => handleCopy(result.solution_section, 'solution')}
                          className="text-purple-400 hover:text-purple-300 text-sm flex items-center space-x-1"
                        >
                          {copiedField === 'solution' ? <CheckCircle size={14} /> : <Copy size={14} />}
                          <span>Copy</span>
                        </button>
                      </div>
                      <p className="text-slate-300 bg-white/5 p-3 rounded-lg text-sm whitespace-pre-wrap">{result.solution_section}</p>
                    </div>

                    {/* Benefits Section */}
                    {result.benefits_section && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Benefits</label>
                          <button
                            onClick={() => handleCopy(formatAsBulletPoints(result.benefits_section), 'benefits')}
                            className="text-purple-400 hover:text-purple-300 text-sm flex items-center space-x-1"
                          >
                            {copiedField === 'benefits' ? <CheckCircle size={14} /> : <Copy size={14} />}
                            <span>Copy</span>
                          </button>
                        </div>
                        <div className="text-emerald-400 bg-emerald-500/10 p-3 rounded-lg text-sm">
                          {renderSection(result.benefits_section)}
                        </div>
                      </div>
                    )}

                    {/* Offer Section */}
                    {result.offer_section && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">What's Included</label>
                          <button
                            onClick={() => handleCopy(result.offer_section, 'offer')}
                            className="text-purple-400 hover:text-purple-300 text-sm flex items-center space-x-1"
                          >
                            {copiedField === 'offer' ? <CheckCircle size={14} /> : <Copy size={14} />}
                            <span>Copy</span>
                          </button>
                        </div>
                        <p className="text-slate-300 bg-white/5 p-3 rounded-lg text-sm whitespace-pre-wrap">{result.offer_section}</p>
                      </div>
                    )}

                    {/* Bonuses Section */}
                    {result.bonuses_section && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Bonuses</label>
                          <button
                            onClick={() => handleCopy(formatAsBulletPoints(result.bonuses_section), 'bonuses')}
                            className="text-purple-400 hover:text-purple-300 text-sm flex items-center space-x-1"
                          >
                            {copiedField === 'bonuses' ? <CheckCircle size={14} /> : <Copy size={14} />}
                            <span>Copy</span>
                          </button>
                        </div>
                        <div className="text-amber-400 bg-amber-500/10 p-3 rounded-lg text-sm">
                          {renderSection(result.bonuses_section)}
                        </div>
                      </div>
                    )}

                    {/* CTA */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">Call to Action</label>
                        <button
                          onClick={() => handleCopy(result.cta_section, 'cta')}
                          className="text-purple-400 hover:text-purple-300 text-sm flex items-center space-x-1"
                        >
                          {copiedField === 'cta' ? <CheckCircle size={14} /> : <Copy size={14} />}
                          <span>Copy</span>
                        </button>
                      </div>
                      <p className="text-green-400 bg-green-500/10 p-3 rounded-lg font-medium">{result.cta_section}</p>
                    </div>

                    {/* FAQs */}
                    {result.faq_suggestions && result.faq_suggestions.length > 0 && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-400 uppercase tracking-wider">FAQ Suggestions</label>
                        <div className="space-y-2">
                          {result.faq_suggestions.map((faq, i) => (
                            <div key={i} className="bg-white/5 p-3 rounded-lg">
                              <p className="text-white font-medium text-sm">{faq.question}</p>
                              <p className="text-slate-400 text-sm mt-1">{faq.answer}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Publishing Instructions - Beginner Friendly */}
                    <div className="space-y-4 pt-4 border-t border-white/10">
                      <div className="flex items-center space-x-2">
                        <Rocket size={20} className="text-purple-400" weight="fill" />
                        <h3 className="text-white font-semibold">Use This To Start Selling</h3>
                      </div>
                      
                      {/* Quick Copy Buttons */}
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleCopy(result.headline, 'quick-headline')}
                          className="px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-sm font-medium flex items-center space-x-1.5 transition-colors"
                        >
                          {copiedField === 'quick-headline' ? <CheckCircle size={14} /> : <Copy size={14} />}
                          <span>Copy Headline</span>
                        </button>
                        <button
                          onClick={() => handleCopy(`${result.subheadline}\n\n${result.problem_section}\n\n${result.solution_section}`, 'quick-desc')}
                          className="px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-sm font-medium flex items-center space-x-1.5 transition-colors"
                        >
                          {copiedField === 'quick-desc' ? <CheckCircle size={14} /> : <Copy size={14} />}
                          <span>Copy Description</span>
                        </button>
                        <button
                          onClick={copyAllSections}
                          className="px-3 py-2 bg-white/10 hover:bg-white/20 text-slate-300 rounded-lg text-sm font-medium flex items-center space-x-1.5 transition-colors"
                        >
                          <Copy size={14} />
                          <span>Copy Full Content</span>
                        </button>
                      </div>

                      {/* Stan Store Option */}
                      <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-3">
                          <span className="px-2 py-0.5 bg-amber-500/30 text-amber-300 text-xs font-semibold rounded uppercase">Recommended</span>
                          <span className="text-white font-medium">Stan Store</span>
                        </div>
                        <ol className="space-y-2 text-sm text-slate-300">
                          <li className="flex items-start space-x-2">
                            <span className="text-amber-400 font-semibold">1.</span>
                            <span>Open <a href="https://stan.store" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300 underline">Stan Store</a> and create a new product</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <span className="text-amber-400 font-semibold">2.</span>
                            <span>Copy your <strong className="text-white">headline</strong> as the product title</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <span className="text-amber-400 font-semibold">3.</span>
                            <span>Paste <strong className="text-white">description</strong> into the product page</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <span className="text-amber-400 font-semibold">4.</span>
                            <span>Set your price and hit <strong className="text-white">Publish</strong></span>
                          </li>
                        </ol>
                      </div>

                      {/* Linktree Option */}
                      <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                        <div className="flex items-center space-x-2 mb-3">
                          <span className="text-white font-medium">Linktree</span>
                          <span className="text-slate-500 text-sm">Quick link option</span>
                        </div>
                        <ol className="space-y-2 text-sm text-slate-300">
                          <li className="flex items-start space-x-2">
                            <span className="text-slate-500 font-semibold">1.</span>
                            <span>Add a new link in your <a href="https://linktr.ee" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 underline">Linktree</a></span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <span className="text-slate-500 font-semibold">2.</span>
                            <span>Use your <strong className="text-white">headline</strong> as the link title</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <span className="text-slate-500 font-semibold">3.</span>
                            <span>Link to your checkout or payment page</span>
                          </li>
                        </ol>
                      </div>

                      <p className="text-slate-500 text-xs text-center">
                        No website needed. These tools handle payments and delivery for you.
                      </p>
                    </div>
                  </>
                )}

                {/* Success Message */}
                {!showLaunchPlan && (
                  <div className="p-4 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30">
                    <div className="flex items-center space-x-3">
                      <CheckCircle size={24} className="text-green-400" weight="fill" />
                      <div>
                        <p className="text-green-400 font-medium">Your sales page is ready!</p>
                        <p className="text-slate-400 text-sm">Now let's launch your offer and get it in front of people.</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Output Actions - Copy, Download PDF, Save */}
                {!showLaunchPlan && (
                  <div className="pt-4 border-t border-white/10">
                    <OutputActions
                      content={`${result.headline}\n\n${result.subheadline}\n\n${result.problem_section}\n\n${result.solution_section}\n\nWhat You Get:\n${result.offer_breakdown}\n\nBonus:\n${result.bonus_section}\n\n${result.cta_section}`}
                      pdfType="sales_page"
                      pdfData={{
                        headline: result.headline,
                        hook: result.subheadline,
                        problem: result.problem_section,
                        solution: result.solution_section,
                        offer_breakdown: result.offer_breakdown,
                        cta: result.cta_section
                      }}
                      saveType="sales_page"
                      saveTitle={result.headline}
                      saveContent={result}
                    />
                  </div>
                )}

                {/* Launch Plan Section */}
                {showLaunchPlan && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="pt-2"
                  >
                    <LaunchPlanCard onAction={handleLaunchAction} onClose={handleClose} />
                  </motion.div>
                )}

                {/* Actions */}
                <div className="flex flex-col space-y-3 pt-4">
                  <button
                    onClick={() => setShowLaunchPlan(!showLaunchPlan)}
                    data-testid="launch-offer-btn"
                    className={`w-full py-4 font-semibold rounded-xl flex items-center justify-center space-x-2 transition-all ${
                      showLaunchPlan
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                        : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white'
                    }`}
                  >
                    <Rocket size={20} weight="fill" />
                    <span>{showLaunchPlan ? 'Hide Launch Plan' : 'Launch My Offer'}</span>
                  </button>
                  
                  {/* Tools to Build This */}
                  <ToolLinks outputType="sales_page" className="my-3" compact />
                  
                  <button
                    onClick={handleCreateAnother}
                    className="w-full py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors"
                  >
                    Create Another
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SalesPageModal;
